import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type InvestorActivityAllocation = {
  id: number;
  debit: number;
  credit: number;
  amount: number;
  entry_type: string;
  entry_date: string | null;
  product_id: number | null;
  product_name?: string | null;
  chassis_no?: string | null;
  profit_distribution_id?: number | null;
  sale_id?: number | null;
  source_product_id?: number | null;
  source_product_name?: string | null;
  destination_product_id?: number | null;
  destination_product_name?: string | null;
  destination_description?: string | null;
  description?: string | null;
  notes?: string | null;
};

export type InvestorReinvestCar = {
  product_id: number | null;
  product_name?: string | null;
  display_name?: string | null;
  description?: string | null;
  chassis_no?: string | null;
  debit: number;
  credit: number;
  net_amount: number;
  entries_count: number;
  is_tbd: boolean;
  status: "open" | "pending";
};

export type InvestorOpenDistribution = {
  id: number;
  sale_id: number;
  product_id: number | null;
  product_name?: string | null;
  final_payable: number;
  paid_amount: number;
  balance: number;
  available: number;
  allocated_active: number;
  status: string;
};

export type InvestorActivity = {
  investor: {
    id: number;
    name?: string | null;
  };
  ledger_balance: number;
  ledger_balance_side: "Dr" | "Cr";
  currency: string;
  totals: {
    reinvested: number;
    cash_paid: number;
    allocation_debit: number;
    allocation_credit: number;
    reinvest_cars_count: number;
    open_payable: number;
    available_balance: number;
    reserved_tbd: number;
    invested_in_vehicles: number;
  };
  reserved_tbd_source_product_id: number | null;
  reinvest_cars: InvestorReinvestCar[];
  allocations: InvestorActivityAllocation[];
  open_distributions: InvestorOpenDistribution[];
};

export function investorActivityErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Could not load investor activity.";
}

function money(n: unknown): number {
  const v = Math.round(Number(n ?? 0) * 100) / 100;
  return Number.isFinite(v) ? v : 0;
}

export async function fetchInvestorActivity(investorId: string | number): Promise<InvestorActivity> {
  const response = await api.get<{ data: InvestorActivity }>(
    `/api/v1/investors/${encodeURIComponent(String(investorId))}/activity`,
  );
  const data = response.data?.data;
  return {
    investor: {
      id: Number(data?.investor?.id ?? investorId),
      name: data?.investor?.name ?? null,
    },
    ledger_balance: money(data?.ledger_balance),
    ledger_balance_side: data?.ledger_balance_side === "Cr" ? "Cr" : "Dr",
    currency: String(data?.currency ?? "PKR"),
    totals: {
      reinvested: money(data?.totals?.reinvested),
      cash_paid: money(data?.totals?.cash_paid),
      allocation_debit: money(data?.totals?.allocation_debit),
      allocation_credit: money(data?.totals?.allocation_credit),
      reinvest_cars_count: Number(data?.totals?.reinvest_cars_count ?? 0),
      open_payable: money(data?.totals?.open_payable),
      available_balance: money(data?.totals?.available_balance ?? data?.totals?.reserved_tbd),
      reserved_tbd: money(data?.totals?.reserved_tbd),
      invested_in_vehicles: money(data?.totals?.invested_in_vehicles),
    },
    reserved_tbd_source_product_id:
      data?.reserved_tbd_source_product_id != null
        ? Number(data.reserved_tbd_source_product_id)
        : null,
    reinvest_cars: Array.isArray(data?.reinvest_cars)
      ? data.reinvest_cars.map((row) => ({
          product_id: row.product_id != null ? Number(row.product_id) : null,
          product_name: row.product_name ?? null,
          display_name: row.display_name ?? row.product_name ?? null,
          description: row.description ?? null,
          chassis_no: row.chassis_no ?? null,
          debit: money(row.debit),
          credit: money(row.credit),
          net_amount: money(row.net_amount),
          entries_count: Number(row.entries_count ?? 0),
          is_tbd: Boolean(row.is_tbd),
          status: row.status === "pending" || row.is_tbd ? "pending" : "open",
        }))
      : [],
    allocations: Array.isArray(data?.allocations)
      ? data.allocations.map((row) => ({
          id: Number(row.id),
          debit: money(row.debit),
          credit: money(row.credit),
          amount: money(row.amount),
          entry_type: String(row.entry_type ?? ""),
          entry_date: row.entry_date ?? null,
          product_id: row.product_id != null ? Number(row.product_id) : null,
          product_name: row.product_name ?? null,
          chassis_no: row.chassis_no ?? null,
          profit_distribution_id:
            row.profit_distribution_id != null ? Number(row.profit_distribution_id) : null,
          sale_id: row.sale_id != null ? Number(row.sale_id) : null,
          source_product_id:
            row.source_product_id != null ? Number(row.source_product_id) : null,
          source_product_name: row.source_product_name ?? null,
          destination_product_id:
            row.destination_product_id != null ? Number(row.destination_product_id) : null,
          destination_product_name: row.destination_product_name ?? null,
          destination_description: row.destination_description ?? null,
          description: row.description ?? null,
          notes: row.notes ?? null,
        }))
      : [],
    open_distributions: Array.isArray(data?.open_distributions)
      ? data.open_distributions.map((row) => ({
          id: Number(row.id),
          sale_id: Number(row.sale_id ?? 0),
          product_id: row.product_id != null ? Number(row.product_id) : null,
          product_name: row.product_name ?? null,
          final_payable: money(row.final_payable),
          paid_amount: money(row.paid_amount),
          balance: money(row.balance),
          available: money(row.available),
          allocated_active: money(row.allocated_active),
          status: String(row.status ?? ""),
        }))
      : [],
  };
}

export type InvestorReservedCashPayload = {
  amount: number;
  payment_date: string;
  payment_method: string | null;
  bank_id?: number | null;
  bank_name?: string | null;
  transaction_reference_no?: string | null;
  pay_order_no?: string | null;
  cheque_no?: string | null;
  deposit_slip_no?: string | null;
  notes?: string | null;
};

export async function payInvestorReservedCash(
  investorId: string | number,
  payload: InvestorReservedCashPayload,
): Promise<unknown> {
  const response = await api.post(
    `/api/v1/investors/${encodeURIComponent(String(investorId))}/pay-reserved-cash`,
    payload,
  );
  return response.data;
}
